import { useContext, useEffect, useState } from "react";
import {
  logCartView,
  logShopPageView,
  logProductViewedEvent,
} from "@pepdirect/v3/helpers/analyticsLogger";
import { LogPageViewParams } from "@pepdirect/v3/types";
import { CartFragment, VariantFragment } from "services/graphql/generated";
import { AnalyticsSessionParamsContext } from "context/analyticsSessionParams";
import { LogContextValueInterface } from "context/log";

export function useLogPageProvider(
  currentUserId: string | null
): LogContextValueInterface {
  /**
   * Wrapper for logPageView, so we don't need to
   * write useEffect on every page.
   * @param key some unique id to identify page,
   * events are expected in a specific order given below
   * pageView -> gtm.js -> productView/cartView
   * for example, page url/slug/whatnot,
   * otherwise it won't trigger when for example
   * route changes from /category/kit-1 to /category/kit-2
   */
  const useLogPageView = (
    params: LogPageViewParams,
    product?: VariantFragment | null,
    cart?: CartFragment | null,
    key?: string
  ): void => {
    const [pageViewLoaded, setPageViewLoaded] = useState(false);
    const { analyticsSessionParams } = useContext(
      AnalyticsSessionParamsContext
    );
    const pageViewKey = key ?? params.id;

    useEffect(() => {
      if (analyticsSessionParams) {
        logShopPageView(
          currentUserId,
          {
            ...params,
            sessionAtributes: {
              ...analyticsSessionParams,
              // UTM attribution persists for the session, but page_location
              // must describe the route being viewed rather than the landing
              // page retained in sessionStorage.
              page_location: window.location.href,
            },
          },
          "poppi shop",
          // The current production container is bootstrapped once in _app.
          // Never race it with the retired fallback GTM-N5CBPB2 container.
          () => undefined
        );
        setPageViewLoaded(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageViewKey, analyticsSessionParams]);

    useEffect(() => {
      if (cart && pageViewLoaded) {
        logCartView(currentUserId, cart);
      }
    }, [cart, pageViewLoaded]);

    useEffect(() => {
      if (product && pageViewLoaded) {
        logProductViewedEvent(currentUserId, product);
      }
    }, [product, pageViewLoaded]);
  };

  return { useLogPageView };
}
